/**
 * AdminEditorialDashboard — the founder's editorial decision engine.
 * Daily workload · smart bulk approvals with written rationale ·
 * actionable publication readiness queue · editorial history + rollback.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, RotateCcw, Zap, Gauge, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  editorialWorkload, editorialBulkSuggestions, editorialDecide,
  editorialReadiness, editorialHistory, editorialRollback, wikiReviewQueue,
} from "../../lib/api";
import RapidTriage from "./RapidTriage";

const PRIORITY = {
  high: { label: "HIGH", color: "#3FB950", hint: "Nearly complete — minor work only" },
  medium: { label: "MEDIUM", color: "#E0A526", hint: "Needs several improvements" },
  low: { label: "LOW", color: "#D92D20", hint: "Requires substantial research" },
};

const SORTS = {
  fastest: { label: "Fastest to Publish", fn: (a, b) => a.estimated_minutes - b.estimated_minutes },
  complete: { label: "Most Complete", fn: (a, b) => b.score - a.score },
  confidence: { label: "Highest Confidence", fn: (a, b) => b.score - a.score },
  sources: { label: "Missing Sources", fn: (a, b) => a.counts.sources - b.counts.sources },
  media: { label: "Missing Media", fn: (a, b) => a.counts.published_media - b.counts.published_media },
  evidence: { label: "Weak Evidence", fn: (a, b) => a.counts.document_previews - b.counts.document_previews },
  timeline: { label: "Timeline Gaps", fn: (a, b) => a.counts.timeline_events - b.counts.timeline_events },
  network: { label: "Missing Relationships", fn: (a, b) => a.counts.entities - b.counts.entities },
  broken: { label: "Broken Sources", fn: (a, b) => b.counts.broken_sources - a.counts.broken_sources },
};

const Stat = ({ label, value, tone }) => (
  <div className="border border-[#2D2A26] bg-[#0d0c0a] px-3 py-2.5 min-w-[110px]">
    <div className="font-display text-2xl leading-none" style={{ color: tone || "#E4DFD3" }}>{value}</div>
    <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#757065] mt-1">{label}</div>
  </div>
);

export const AdminEditorialDashboard = ({ token, userToken }) => {
  const [work, setWork] = useState(null);
  const [bulk, setBulk] = useState([]);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [sort, setSort] = useState("fastest");
  const [onlyUnready, setOnlyUnready] = useState(true);
  const [busy, setBusy] = useState("");
  const [triage, setTriage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, b, r, h] = await Promise.all([
        editorialWorkload(token, userToken),
        editorialBulkSuggestions(token, userToken),
        editorialReadiness(token, userToken),
        editorialHistory(token, userToken),
      ]);
      setWork(w); setBulk(b.suggestions || []);
      setRows(r.dossiers || []); setHistory(h.history || []);
    } catch { toast.error("Failed to load editorial workload"); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);

  const approveBatch = async (g) => {
    setBusy(g.id);
    try {
      const r = await editorialDecide(token, g.ids, "approve", `bulk: ${g.title}`, userToken);
      toast("BATCH PUBLISHED", { description: `${r.ok} assets are now live across the Archive.` });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(""); }
  };

  const rollback = async (h) => {
    try {
      const r = await editorialRollback(token, h.id, userToken);
      toast("ROLLED BACK", { description: `Asset restored to ${r.restored_to}.` });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rollback failed"); }
  };

  const openTriage = async () => {
    try {
      const q = await wikiReviewQueue(token, userToken);
      if (!q.queue?.length) { toast("Queue clear — nothing to triage."); return; }
      setTriage(q.queue);
    } catch { toast.error("Could not load the review queue"); }
  };

  const sorted = useMemo(() => {
    const list = rows.filter((r) => !onlyUnready || !r.ready);
    return [...list].sort(SORTS[sort].fn);
  }, [rows, sort, onlyUnready]);

  if (!work) {
    return <div className="flex items-center gap-2 font-mono text-[11px] text-[#757065] p-6">
      <Loader2 size={13} className="animate-spin" /> Preparing the editorial workload…
    </div>;
  }

  return (
    <div className="space-y-8 max-w-full overflow-x-hidden" data-testid="editorial-dashboard">
      {/* ── DAILY WORKLOAD ── */}
      <section>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#D92D20]">
            Today&apos;s Editorial Workload
          </h2>
          <button onClick={openTriage} data-testid="open-rapid-triage"
                  className="ml-auto inline-flex items-center gap-1.5 bg-[#D92D20] text-black px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#b8261b]">
            <Zap size={11} /> Rapid Triage ({work.queue.total})
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Stat label="Awaiting review" value={work.queue.total} tone="#E0A526" />
          <Stat label="Portraits" value={work.queue.portraits} />
          <Stat label="Evidence" value={work.queue.evidence} />
          <Stat label="High confidence" value={work.queue.high_confidence} tone="#3FB950" />
          <Stat label="Low confidence" value={work.queue.low_confidence} tone="#D92D20" />
          <Stat label="Ready to publish" value={work.publishing.ready_now} tone="#3FB950" />
          <Stat label="Ready ≤ 6 min" value={work.publishing.ready_within_6_min} tone="#5DD4B3" />
          <Stat label="Broken sources" value={work.hygiene.broken_sources}
                tone={work.hygiene.broken_sources ? "#D92D20" : undefined} />
          <Stat label="Decided today" value={work.decided_today} />
        </div>
      </section>

      {/* ── SMART BULK ACTIONS ── */}
      {bulk.length > 0 && (
        <section data-testid="bulk-suggestions">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A68F5E]">
            Suggested Batches — Oracle-verified safe
          </h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {bulk.map((g) => (
              <div key={g.id} className="border border-[#2D2A26] bg-[#0d0c0a] p-3.5 flex flex-col"
                   data-testid={`bulk-card-${g.id}`}>
                <div className="text-[13.5px] text-[#E4DFD3] font-heading">{g.title}</div>
                <ul className="mt-2 space-y-1 flex-1">
                  {g.reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[10.5px] text-[#A39E93] leading-snug">
                      <ShieldCheck size={9} className="mt-[2px] shrink-0 text-[#3FB950]" /> {r}
                    </li>
                  ))}
                </ul>
                <button onClick={() => approveBatch(g)} disabled={!!busy}
                        data-testid={`bulk-approve-${g.id}`}
                        className="mt-3 inline-flex items-center justify-center gap-1.5 bg-[#1d3b2a] border border-[#3FB950]/50 text-[#5DD4B3] py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#255033] disabled:opacity-50">
                  {busy === g.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                  Approve all {g.ids.length}
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── PUBLICATION QUEUE ── */}
      <section data-testid="publication-queue">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A68F5E]">
            Publication Queue
          </h2>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
                  data-testid="queue-sort"
                  className="ml-auto bg-[#151310] border border-[#3A3630] text-[#E4DFD3] font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-1.5">
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#757065]">
            <input type="checkbox" checked={onlyUnready} onChange={(e) => setOnlyUnready(e.target.checked)}
                   className="accent-[#D92D20]" /> hide ready
          </label>
        </div>
        <div className="mt-3 space-y-2">
          {sorted.slice(0, 40).map((r) => {
            const p = PRIORITY[r.priority];
            return (
              <div key={r.slug} className="border border-[#2D2A26] bg-[#0d0c0a] p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                   data-testid={`readiness-row-${r.slug}`}>
                <div className="w-14 shrink-0 text-center">
                  <div className="font-display text-2xl leading-none"
                       style={{ color: r.ready ? "#3FB950" : p.color }}>{r.score}</div>
                  <div className="font-mono text-[7px] uppercase tracking-[0.2em] text-[#5A554C] mt-0.5">score</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/topic/${r.slug}`} target="_blank"
                          className="text-[13.5px] text-[#E4DFD3] font-heading hover:text-[#D92D20]">
                      {r.title}
                    </Link>
                    <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 border"
                          style={{ color: p.color, borderColor: `${p.color}55` }} title={p.hint}>
                      {p.label}
                    </span>
                    {r.held_candidates > 0 && (
                      <span className="font-mono text-[8px] text-[#E0A526]">{r.held_candidates} held media</span>
                    )}
                  </div>
                  <div className="mt-1 font-mono text-[9.5px] leading-snug"
                       style={{ color: r.ready ? "#5DD4B3" : "#A39E93" }}>
                    {r.ready ? "READY FOR PUBLICATION — publish now." :
                      <>READY IN ~{r.estimated_minutes} MIN · {r.recommendation}</>}
                  </div>
                </div>
                <div className="shrink-0 font-mono text-[8px] text-[#5A554C] text-right">
                  <div>last reviewed</div>
                  <div className="text-[#757065]">{(r.last_reviewed || "").slice(0, 10) || "—"}</div>
                </div>
              </div>
            );
          })}
          {sorted.length === 0 && (
            <div className="font-mono text-[10px] text-[#757065] italic p-4 border border-dashed border-[#2D2A26]">
              Every dossier in this view is publication-ready.
            </div>
          )}
        </div>
      </section>

      {/* ── EDITORIAL HISTORY ── */}
      <section data-testid="editorial-history">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#A68F5E]">
          Editorial History
        </h2>
        <div className="mt-3 space-y-1.5">
          {history.slice(0, 25).map((h) => (
            <div key={h.id} className="border border-[#2D2A26] bg-[#0d0c0a] px-3 py-2 flex items-center gap-3"
                 data-testid={`history-row-${h.id}`}>
              <span className="font-mono text-[9px] uppercase tracking-[0.15em] w-16 shrink-0"
                    style={{ color: h.action === "approve" ? "#5DD4B3" : h.action === "reject" ? "#FFA9A0" : "#9FA8D4" }}>
                {h.action}
              </span>
              <span className="text-[11px] text-[#A39E93] truncate flex-1">
                {h.caption || h.asset_id}
                {h.target_id && <span className="text-[#5A554C]"> → {h.target_id}</span>}
              </span>
              {h.readiness_before && h.readiness_after && h.readiness_before.score !== h.readiness_after.score && (
                <span className="font-mono text-[8px] text-[#5DD4B3] shrink-0">
                  {h.readiness_before.score}→{h.readiness_after.score}
                </span>
              )}
              <span className="font-mono text-[8px] text-[#5A554C] shrink-0 hidden sm:inline">
                {(h.at || "").slice ? String(h.at).slice(0, 16).replace("T", " ") : ""}
              </span>
              {h.action !== "rollback" && (
                <button onClick={() => rollback(h)} title="Roll back this decision"
                        data-testid={`rollback-${h.id}`}
                        className="shrink-0 text-[#757065] hover:text-[#E0A526]">
                  <RotateCcw size={11} />
                </button>
              )}
            </div>
          ))}
          {history.length === 0 && (
            <div className="font-mono text-[10px] text-[#757065] italic">No editorial decisions logged yet.</div>
          )}
        </div>
      </section>

      {triage && (
        <RapidTriage queue={triage} token={token} userToken={userToken}
                     onClose={() => { setTriage(null); load(); }} />
      )}
    </div>
  );
};

export default AdminEditorialDashboard;
